/**
 * חיבור למקור נתונים — one connection: configure it, test it, and map the companies behind it.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS IS A SCREEN AND NOT A PANEL ON THE LIST
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `SourceConnectionsScreen` is a list of connections and their state. It answers "which of my
 * connections are broken". This one answers "what is wrong with THIS one and how do I fix it", and
 * that is three forms and a table — 09 §2.1 fails three of the six modal tests here (M2, six fields;
 * M4, a table read row by row; M6, a decision per row that opens another), so it is a child screen
 * with an address rather than a modal, and rather than a fourth accordion on a list that already has
 * four cards.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE FOUR THINGS THIS SCREEN REFUSES TO COLLAPSE
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ONE — CONFIGURATION AND CONTACT ARE TWO QUESTIONS. `לא הוגדר` is not a kind of `תקלה` (07 N-5) and
 * `מעולם לא נוצר קשר` is not a kind of `הקשר נפסק`. Each has its own block on this screen and its own
 * next step, because each is a different person's job on a different day.
 *
 * TWO — CONTACT IS NOT FRESHNESS. The health block says so in words, every time, on every state. A
 * green connector above a stale trial balance is the June 2026 freeze exactly, and the health payload
 * carries no data watermark at all so the screen could not conflate them even if it tried.
 *
 * THREE — A FAILED TEST IS A DIAGNOSIS ABOUT THE CUSTOMER'S SYSTEM, NOT OUR ERROR STATE. The probe
 * route answers 200 with `ok: false` for exactly this reason, and the result is rendered as a finding
 * with its failure kind — never as "אירעה תקלה".
 *
 * FOUR — AN UNMAPPED COMPANY IS A QUEUE ITEM. 07 L-3: seven paid-for companies were skipped every
 * night and the skip lived in a `_meta` array inside a 47 MB file. Here the count is a sentence and
 * every unmapped row has a control beside it.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * AND WHAT IS STRUCTURALLY ABSENT
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * No host, no port, no connection string, no credential. The two reference fields carry a NAME that
 * deployment configuration resolves (07 §2.3, N-6), the form controls do not even carry the wire
 * field names, and the mapping request has no place to put a tenant or a company — the gate resolves
 * the company from the address it was called on, which is what makes a cross-tenant binding unsayable
 * rather than merely refused.
 *
 * The external company ids are rendered LTR and passed through UNCHANGED. 05 R-5: the twelve
 * near-miss pairs are proven distinct clients, and any normalisation that folded them destroys one.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { DataTable } from '../components/DataTable';
import { FactList } from '../components/FactList';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type {
  ExternalCompanySummary,
  IdentifierConfidence,
  ProbeOutcome,
  SourceConfigurationRefusal,
  SourceFailureKind,
} from '../api/contract';
import { IDENTIFIER_CONFIDENCES, SOURCE_DATE_FORMATS, SOURCE_FAILURE_KINDS } from '../api/contract';
import { connectorStatusHe } from '../api/adminVocabulary';
import {
  CONFIGURATION_FIELDS,
  bindingRefusalText,
  configurationBody,
  configurationRefusalText,
  healthText,
  identifierConfidenceHe,
  probeText,
  sourceDateFormatHe,
  unmappedQueueHe,
} from '../api/sourceAdminText';
import {
  completenessTextFor,
  freshnessTextFor,
  missingConfigurationHe,
  sourceStatusTextFor,
} from '../api/sourceStatusText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import {
  fixtureConnectionHealth,
  fixtureExternalCompanies,
  fixtureSourceConnections,
  fixturesEnabled,
} from '../api/fixtures';
import { routes } from '../routes';

export interface SourceConnectionScreenProps {
  readonly connectionUid: string;
}

const TITLE_HE = 'חיבור למקור נתונים';

/** Every completed action on this screen, and the outcome it produced. One union, one renderer. */
type Outcome =
  | { readonly kind: 'configured' }
  | { readonly kind: 'probed'; readonly result: ProbeOutcome }
  | { readonly kind: 'mapped'; readonly externalCompanyId: string }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function isFailureKind(value: string | undefined): value is SourceFailureKind {
  return value !== undefined && (SOURCE_FAILURE_KINDS as readonly string[]).includes(value);
}

export function SourceConnectionScreen({ connectionUid }: SourceConnectionScreenProps): JSX.Element {
  const ctx = useWorkContext();
  const [values, setValues] = useState<Record<string, string>>({});
  const [mappingFor, setMappingFor] = useState<ExternalCompanySummary | null>(null);
  const [mapCompanyUid, setMapCompanyUid] = useState('');
  const [mapConfidence, setMapConfidence] = useState<IdentifierConfidence>('human_confirmed');
  const [mapNote, setMapNote] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  const connection =
    (fixturesEnabled() ? fixtureSourceConnections : []).find(
      (c) => c.connectionUid === connectionUid,
    ) ?? null;
  const back = { labelHe: 'מקורות נתונים', href: routes.tenantSources };

  if (connection === null) {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <StateBlock
          titleHe="החיבור הזה אינו זמין"
          bodyHe="לא הצלחנו להציג את החיבור שאליו מפנה הקישור."
          tone="unavailable"
          nextStep={
            <a className="btn btn--primary" href={back.href}>
              חזרה לרשימת מקורות הנתונים
            </a>
          }
        />
      </AppShell>
    );
  }

  const status = sourceStatusTextFor(connection);
  const connector = connectorStatusHe(connection.connectorStatus);
  const freshness = freshnessTextFor(connection.freshness);
  const completeness = completenessTextFor(connection.completeness);
  const missing = missingConfigurationHe(connection.missingConfiguration);
  const health =
    (fixturesEnabled() ? fixtureConnectionHealth : []).find(
      (h) => h.connectionUid === connectionUid,
    ) ?? null;
  const externals = fixturesEnabled() ? fixtureExternalCompanies : [];
  const unmapped = externals.filter((e) => e.boundCompanyUid === null);

  /** Turns a conflict body into the connection's own words. Never renders a member of the body. */
  function renderSourceConflict(failure: Extract<ActionFailure, { kind: 'conflict' }>): JSX.Element {
    const refusal: SourceConfigurationRefusal = {
      reason: failure.body.error,
      missingConfiguration: failure.body.missingConfiguration ?? [],
      remedy: failure.body.remedy ?? null,
    };
    // `unmapped`, `ambiguous`, `not_found` and `unavailable` are the binding vocabulary; anything
    // else on this screen is a configuration or probe refusal.
    if (
      failure.body.error === 'unmapped' ||
      failure.body.error === 'ambiguous' ||
      failure.body.error === 'not_found' ||
      failure.body.error === 'unavailable'
    ) {
      const text = bindingRefusalText({
        reason: failure.body.error,
        resolvingFactRequired: failure.body.resolvingFactRequired ?? null,
        candidateCount: failure.body.candidateCount ?? 0,
      });
      return (
        <section className="denial" data-tone="blocked" role="alert">
          <h2 className="denial__title">{text.titleHe}</h2>
          <p className="denial__explanation">{text.explanationHe}</p>
          {text.candidatesHe === null ? null : (
            <p className="denial__explanation">{text.candidatesHe}</p>
          )}
          {text.resolvingFactHe === null ? null : (
            <p className="denial__explanation">{text.resolvingFactHe}</p>
          )}
          <p className="denial__next-step">{text.nextStepHe}</p>
        </section>
      );
    }
    const text = configurationRefusalText(refusal);
    return (
      <section className="denial" data-tone="blocked" role="alert">
        <h2 className="denial__title">{text.titleHe}</h2>
        <p className="denial__explanation">{text.explanationHe}</p>
        <p className="denial__next-step">{text.nextStepHe}</p>
      </section>
    );
  }

  async function run(action: () => Promise<Outcome>): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      setOutcome(await action());
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  function saveConfiguration(e: FormEvent): void {
    e.preventDefault();
    void run(async () => {
      // The wire names never touch the DOM — `configurationBody` is the one place they are used.
      await callOperation('tenant.source.configure', {
        params: { connection_uid: connectionUid },
        body: configurationBody(values),
      });
      return { kind: 'configured' };
    });
  }

  function probe(): void {
    void run(async () => {
      // A POST that declares `effects: 'READ'` on the server, truthfully: 07 A2, a probe never
      // writes. It is a POST because it costs an external round trip, not because it mutates.
      const result = await callOperation('tenant.source.probe', {
        params: { connection_uid: connectionUid },
        body: {},
      });
      if (result.ok) {
        return { kind: 'probed', result: { ok: true, checkedAtUtc: result.checkedAt ?? null } };
      }
      const kind = result.failure?.kind;
      return {
        kind: 'probed',
        result: {
          ok: false,
          // A kind this build has no Hebrew for becomes the generic adapter fault rather than an
          // English string on a customer's screen (09 §9.1 V3).
          failureKind: isFailureKind(kind) ? kind : 'ADAPTER_FAULT',
        },
      };
    });
  }

  function submitMapping(e: FormEvent): void {
    e.preventDefault();
    const external = mappingFor;
    if (external === null) return;
    void run(async () => {
      // THE COMPANY IS IN THE ADDRESS, NOT IN THE BODY. The gate resolves it inside this tenant at
      // stage 4, which is what makes a cross-tenant binding unsayable rather than merely refused.
      await callOperation('companies.source_binding.create', {
        params: { company_uid: mapCompanyUid },
        body: {
          connectionUid,
          // 05 R-5 / 07 L-4: passed through unchanged. Never trimmed, never case-folded.
          externalCompanyId: external.externalCompanyId,
          confidence: mapConfidence,
          note: mapNote,
          taxIdCheckDigitValid: null,
        },
      });
      return { kind: 'mapped', externalCompanyId: external.externalCompanyId };
    });
    setMappingFor(null);
  }

  const columns: readonly Column<ExternalCompanySummary>[] = [
    {
      key: 'company',
      headerHe: 'החברה במערכת המקור',
      render: (e) => (
        <span className="company-cell">
          <span className="company-cell__name">{e.displayName}</span>
          {/* LTR under an explicit dir, and unchanged. 09 §9.5 A1: bidi is controlled, never guessed. */}
          <span className="company-cell__id" dir="ltr">
            {e.externalCompanyId}
          </span>
        </span>
      ),
    },
    {
      key: 'bound',
      headerHe: 'מקושרת לחברה במשרד',
      render: (e) =>
        e.boundCompanyUid === null ? (
          <span className="cell-note">אינה מקושרת — נתוניה אינם נקלטים</span>
        ) : (
          (ctx.visibleCompanies.find((c) => c.companyUid === e.boundCompanyUid)?.legalName ??
            'מקושרת לחברה שאינך רשאי לראות')
        ),
    },
    {
      key: 'actions',
      headerHe: 'פעולות',
      render: (e) =>
        e.boundCompanyUid === null ? (
          <button type="button" className="btn btn--quiet" onClick={() => setMappingFor(e)}>
            קישור לחברה
          </button>
        ) : null,
    },
  ];

  return (
    <AppShell
      titleHe={connection.displayNameHe}
      backToParent={back}
      provenanceHe="בשדות ההפניה נשמר שם בלבד. כתובת, סיסמה או מחרוזת התחברות אינן נשמרות במערכת בשום שלב."
    >
      <div className="chip-row">
        <StatusChip labelHe={status.labelHe} tone={status.tone} />
        <StatusChip labelHe={connector.labelHe} tone={connector.tone} />
        {connection.origin === 'development_fixture' ? (
          <StatusChip labelHe="מקור פיתוח — אינו מערכת אמת" tone="warn" />
        ) : null}
      </div>

      <p className="summary-line">{status.explanationHe}</p>
      {missing === '' ? null : <p className="connection__missing">{missing}</p>}

      <FactList
        facts={[
          {
            labelHe: 'טריות הנתונים',
            value: (
              <span className="company-cell">
                <span className="company-cell__name">{freshness.asOfHe}</span>
                {freshness.fetchedHe === null ? null : (
                  <span className="company-cell__id">{freshness.fetchedHe}</span>
                )}
              </span>
            ),
          },
          {
            labelHe: 'שלמות הנתונים',
            value: `${completeness.labelHe} — ${completeness.explanationHe}`,
          },
          { labelHe: 'חברות מקושרות', value: <span dir="ltr">{connection.boundCompanyCount}</span> },
        ]}
      />

      {/* ── CONTACT, which is a different question from freshness and says so ───────────────── */}
      {health === null ? null : (
        <section className="notice" data-tone="info">
          <h2 className="notice__title">{`מצב הקשר עם תוכנת החיבור — ${healthText(health).titleHe}`}</h2>
          <p className="notice__body">{healthText(health).explanationHe}</p>
          {healthText(health).lastSeenHe === null ? null : (
            <p className="notice__body">{healthText(health).lastSeenHe}</p>
          )}
          {healthText(health).degradationsHe.length === 0 ? null : (
            <ul className="role__capabilities">
              {healthText(health).degradationsHe.map((line) => (
                <li key={line} className="role__capability">
                  {line}
                </li>
              ))}
            </ul>
          )}
          {/* Always present. The payload carries the flag so a consumer cannot claim it was not told. */}
          <p className="notice__body">{healthText(health).contactIsNotFreshnessHe}</p>
          <p className="notice__body">{healthText(health).nextStepHe}</p>
        </section>
      )}

      {/* ── CONFIGURE ──────────────────────────────────────────────────────────────────────── */}
      <form className="form" onSubmit={saveConfiguration}>
        <h2 className="form__title">הגדרת החיבור</h2>
        <p className="form__lead">
          כל הפרטים נשמרים יחד. הגדרה חלקית אינה נשמרת, כדי שחיבור לא יתנהג כאילו הוגדר בזמן שחסרים
          לו פרטים.
        </p>

        {CONFIGURATION_FIELDS.map((field) =>
          field.formName === 'date-format' ? (
            <fieldset key={field.formName} className="field field--choice">
              <legend className="field__label">{field.labelHe}</legend>
              {SOURCE_DATE_FORMATS.map((format) => (
                <label key={format} className="choice">
                  <input
                    type="radio"
                    name={field.formName}
                    value={format}
                    checked={values[field.formName] === format}
                    onChange={() => setValues((v) => ({ ...v, [field.formName]: format }))}
                  />
                  <span>{sourceDateFormatHe(format)}</span>
                </label>
              ))}
              <span className="field__hint">{field.hintHe}</span>
            </fieldset>
          ) : (
            <label key={field.formName} className="field">
              <span className="field__label">{field.labelHe}</span>
              <input
                className="field__input"
                name={field.formName}
                value={values[field.formName] ?? ''}
                onChange={(e) =>
                  setValues((v) => ({ ...v, [field.formName]: e.currentTarget.value }))
                }
              />
              <span className="field__hint">{field.hintHe}</span>
            </label>
          ),
        )}

        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending}>
            {sending ? 'שולח…' : 'שמירת ההגדרה'}
          </button>
          <button type="button" className="btn" onClick={probe} disabled={sending}>
            בדיקת חיבור
          </button>
        </div>
        <p className="form__note">
          בדיקת החיבור פונה למערכת המקור ואינה משנה בה דבר. הפנייה נרשמת ביומן הביקורת של המשרד.
        </p>
      </form>

      {/* ── THE OUTCOME OF WHATEVER WAS JUST DONE ──────────────────────────────────────────── */}
      {outcome === null ? null : outcome.kind === 'configured' ? (
        <StateBlock
          titleHe="ההגדרה נשמרה"
          bodyHe="פרטי החיבור נשמרו. הרצת בדיקת חיבור תראה אם המערכת שבצד השני עונה."
          nextStep={<p>שמירת הגדרה אינה מוכיחה שהחיבור עובד — לכך יש להריץ בדיקה.</p>}
        />
      ) : outcome.kind === 'probed' ? (
        (() => {
          const text = probeText(outcome.result, (kind) => {
            const asConnection = sourceStatusTextFor({ ...connection, failureKind: kind });
            return {
              explanationHe: asConnection.explanationHe,
              nextStepHe: asConnection.nextStepHe,
            };
          });
          return (
            <StateBlock
              titleHe={text.titleHe}
              bodyHe={text.explanationHe}
              tone={text.tone === 'ok' ? 'empty' : 'error'}
              nextStep={<p>{text.nextStepHe}</p>}
            />
          );
        })()
      ) : outcome.kind === 'mapped' ? (
        <StateBlock
          titleHe="הקישור נרשם"
          bodyHe="החברה שבמערכת המקור מקושרת כעת לחברה במשרד, ונתוניה ייקלטו מכאן ואילך."
          nextStep={<p>הקישור נרשם ביומן הביקורת יחד עם מי שביצע אותו ועם רמת הוודאות שנבחרה.</p>}
        />
      ) : (
        <ActionFailureNotice failure={outcome.failure} renderConflict={renderSourceConflict} />
      )}

      {/* ── THE ONBOARDING QUEUE ───────────────────────────────────────────────────────────── */}
      <p className="summary-line">{unmappedQueueHe(unmapped.length, externals.length)}</p>

      <DataTable<ExternalCompanySummary>
        captionHe="חברות במערכת המקור"
        columns={columns}
        rows={externals}
        rowKey={(e) => e.externalCompanyId}
        filterSummaryHe={`מציג ${externals.length} חברות שהחיבור מדווח עליהן · ${unmapped.length} מהן אינן מקושרות`}
        emptyStateHe="החיבור לא דיווח על אף חברה."
      />

      {/* ── MAPPING ONE ROW. A form, not a modal: it opens a decision with a stated consequence. */}
      {mappingFor === null ? null : (
        <form className="form" onSubmit={submitMapping}>
          <h2 className="form__title">{`קישור החברה ${mappingFor.displayName}`}</h2>
          <p className="form__lead">
            המזהה במערכת המקור נשמר כפי שהוא, בלי לשנות בו דבר. מזהים דומים אינם אותו מזהה, וקישור
            שגוי מכניס נתונים של לקוח אחד לתיק של אחר.
          </p>

          <fieldset className="field field--choice">
            <legend className="field__label">החברה במשרד</legend>
            {ctx.visibleCompanies.map((company) => (
              <label key={company.companyUid} className="choice">
                <input
                  type="radio"
                  name="map-company"
                  value={company.companyUid}
                  checked={mapCompanyUid === company.companyUid}
                  onChange={() => setMapCompanyUid(company.companyUid)}
                />
                <span>
                  {company.legalName}
                  <span className="choice__hint">{company.registrationNumber ?? 'ללא ח.פ רשום'}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <fieldset className="field field--choice">
            <legend className="field__label">רמת הוודאות של הקישור</legend>
            {IDENTIFIER_CONFIDENCES.map((confidence) => (
              <label key={confidence} className="choice">
                <input
                  type="radio"
                  name="map-confidence"
                  value={confidence}
                  checked={mapConfidence === confidence}
                  onChange={() => setMapConfidence(confidence)}
                />
                <span>{identifierConfidenceHe(confidence)}</span>
              </label>
            ))}
            <span className="field__hint">
              רמת הוודאות נשמרת עם הקישור וקובעת אם אפשר להסתמך עליו. היא אינה אחוז ואינה הערכה
              חופשית — זו רשימה סגורה.
            </span>
          </fieldset>

          <label className="field">
            <span className="field__label">הערה</span>
            <input
              className="field__input"
              name="map-note"
              value={mapNote}
              onChange={(e) => setMapNote(e.currentTarget.value)}
            />
            <span className="field__hint">מה מבסס את הקישור. נשמר יחד איתו ביומן.</span>
          </label>

          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={sending || mapCompanyUid === ''}
            >
              רישום הקישור
            </button>
            <button type="button" className="btn" onClick={() => setMappingFor(null)}>
              ביטול
            </button>
          </div>
        </form>
      )}
    </AppShell>
  );
}
